import PrinterService from "../../Services/PrinterService";
import {
  Alignment,
  BARCODE_TYPE,
  Orientation,
  ZebraBarcode,
  ZebraGraphicBox,
  ZebraImage,
  ZebraItem,
  ZebraText,
  ZebraTextBlock,
} from "../../Models/Zebra";
import moment from "moment";
import Utils from "../../Utils/Utils";
import { Label, ProducerType } from "../../Models/Label";
import { LabelHistoryDTO } from "../../Models/LabelHistory";
import imageToZ64 from "../../Utils/ZPLImage";

const mmToInch = Utils.mmToInch;
const ptToInch = Utils.ptToInch;
const calcDots = Utils.calcDots;

export type TemplateType = (label: Label, dto: LabelHistoryDTO) => string;

const Template: TemplateType = (label: Label, dto: LabelHistoryDTO): string => {
  const labelWidth = 85; //label.type.width; //mm
  const labelHeight = 100; //label.type.height; //mm
  const dpi = 300;
  const x = Math.round(mmToInch(11) * dpi); // don't use it yet
  const y = Math.round(mmToInch(label.titleLines == 2 ? 15 : 17) * dpi); // 20 mm kõrgusel, 25.4 = mm to inch
  const height = Math.round(ptToInch(16) * dpi);
  const labelWidthDots = Math.round(mmToInch(labelWidth) * dpi);
  const labelHeightDots = Math.round(mmToInch(labelHeight) * dpi);
  const requiredDots = 54;
  const rightX = 50;
  const positionAdjust = dto.position !== undefined ? Number(dto.position) : 0;

  const getPackagerTitle = () => {
    const title = label.producerType;
    if (title === ProducerType.IMPORTER) {
      return "Importer";
    }
    if (title === ProducerType.PACKAGER) {
      return "Packager";
    }
    if (title === ProducerType.PRODUCER) {
      return "Producer";
    }
    if (title === ProducerType.EXPORTER) {
      return "Exporter";
    }
    if (title === ProducerType.DISTRIBUTOR) {
      return "Distributor";
    }
  };

  const translatedOrigin = () => {
    const o = dto.origin;
    if (o === "Eesti") {
      return "Estonia";
    }
    if (o === "Rootsi") {
      return "Sweden";
    }
    if (o === "Poola") {
      return "Poland";
    }
    if (o === "Soome") {
      return "Finland";
    }
    if (o === "Leedu") {
      return "Lithuania";
    }
    if (o === "Läti") {
      return "Latvia";
    }
    return o;
  };

  const translatedOriginEco = () => {
    const o = dto.origin;
    if (o === "Eesti") {
      return "Estonian";
    }
    if (o === "Rootsi") {
      return "Swedish";
    }
    return o;
  };

  const title: ZebraTextBlock = {
    alignment: Alignment.LEFT,
    maxLines: label.titleLines,
    content: label.title,
    position: { x: x - Math.round(mmToInch(1) * dpi), y },
    size: { height },
    maxWidth: Math.round(mmToInch(65) * dpi),
    type: "ZebraTextBlock",
  };

  const supplierId: ZebraText = {
    content: "Supplier id: ",
    position: { x: x, y: Math.round(mmToInch(33.565) * dpi) },
    size: { height: Math.round(ptToInch(12) * dpi) },
    type: "ZebraText",
  };

  // const img = document.getElementById("organic");
  const img = document.getElementById("organic_203");
  const organicFlag = imageToZ64(img);
  let zpl = `^GFA,${organicFlag.length},${organicFlag.length},${organicFlag.rowlen},${organicFlag.z64}`;
  const img2 = document.getElementById("test_label");
  const img2Z = imageToZ64(img2, { notrim: true });
  let zpl2 = `^GFA,${img2Z.length},${img2Z.length},${img2Z.rowlen},${img2Z.z64}`;

  const flag: ZebraImage = {
    content: zpl,
    type: "ZebraImage",
    position: {
      x: Math.round(mmToInch(labelWidth - 13.5 - 10) * dpi),
      y: Math.round(mmToInch(58) * dpi),
    },
    size: supplierId.size,
  };

  const pngimg: ZebraImage = {
    content: zpl2,
    type: "ZebraImage",
    position: { x: 0, y: 0 },
    size: {
      width: labelWidth,
      height: labelHeight,
    },
  };

  const eeoko03: ZebraTextBlock = {
    content: `EE-ÖKO-03\\& ${translatedOriginEco()} agriculture`,
    size: { height: Math.round(ptToInch(4) * dpi) },
    position: {
      x: flag.position.x,
      y: flag.position.y + Math.round(mmToInch(10) * dpi),
    },
    maxLines: 2,
    maxWidth: Math.round(mmToInch(15.5) * dpi),
    alignment: Alignment.LEFT,
    type: "ZebraTextBlock",
  };

  const box: ZebraGraphicBox = {
    size: {
      width: Math.round(mmToInch(40) * dpi),
      height: Math.round(mmToInch(15) * dpi),
    },
    thickness: 3,
    position: { x, y: supplierId.position.y + supplierId.size.height },
    type: "ZebraGraphicBox",
  };
  const nutriDataTitle: ZebraText = {
    content: "Nutritional information per 100g",
    position: { x: x, y: Math.round(mmToInch(33.565) * dpi) },
    size: { height: Math.round(ptToInch(7) * dpi) },
    type: "ZebraText",
  };

  const energyChars = "Energy ";
  const nutriDataEnergy: ZebraText = {
    content: "Energy " + ".".repeat(calcDots(energyChars, requiredDots - 7)),
    position: { x: x, y: Math.round(mmToInch(37.8) * dpi) },
    size: { height: Math.round(ptToInch(6) * dpi) },
    type: "ZebraText",
  };

  const nutriDataEnergyValue: ZebraTextBlock = {
    alignment: Alignment.RIGHT,
    maxLines: 1,
    content:
      label.product.energyKcal + " kcal/" + label.product.energyKj + " kJ",
    position: {
      x: x + Math.round(mmToInch(rightX) * dpi),
      y: nutriDataEnergy.position.y,
    },
    size: { height: nutriDataEnergy.size.height },
    maxWidth: (190 * dpi) / 300,
    type: "ZebraTextBlock",
  };

  const fatChars = "Total Fat ";
  const nutriDataFat: ZebraText = {
    content: "Total Fat " + ".".repeat(calcDots(fatChars, requiredDots)),
    position: {
      x: x,
      y: Math.round(nutriDataEnergy.position.y + nutriDataEnergy.size.height),
    },
    size: { height: Math.round(ptToInch(6) * dpi) },
    type: "ZebraText",
  };

  const nutriDataFatValue: ZebraTextBlock = {
    ...nutriDataEnergyValue,
    content: label.product.fat + " g",
    position: {
      x: x + Math.round(mmToInch(rightX) * dpi),
      y: nutriDataFat.position.y,
    },
  };

  const satFatChars = "Saturated Fat";
  const nutriDataSatFat: ZebraText = {
    content: "Saturated Fat " + ".".repeat(calcDots(satFatChars, requiredDots)),
    position: {
      x: x,
      y: Math.round(nutriDataFat.position.y + nutriDataFat.size.height),
    },
    size: { height: Math.round(ptToInch(6) * dpi) },
    type: "ZebraText",
  };

  const nutriDataSatFatValue: ZebraTextBlock = {
    ...nutriDataEnergyValue,
    content: label.product.satFat + " g",
    position: {
      x: x + Math.round(mmToInch(rightX) * dpi),
      y: nutriDataSatFat.position.y,
    },
  };

  const carbs = "Total Carbohydrate ";
  const nutriDataCarbs: ZebraText = {
    content: carbs + ".".repeat(calcDots(carbs, requiredDots)),
    position: {
      x: x,
      y: Math.round(nutriDataSatFat.position.y + nutriDataSatFat.size.height),
    },
    size: { height: Math.round(ptToInch(6) * dpi) },
    type: "ZebraText",
  };

  const nutriDataCarbsValue: ZebraTextBlock = {
    ...nutriDataEnergyValue,
    content: label.product.carbs + " g",
    position: {
      x: x + Math.round(mmToInch(rightX) * dpi),
      y: nutriDataCarbs.position.y,
    },
  };

  const sugars = "of which Sugars ";
  const nutriDataSugars: ZebraText = {
    content: sugars + ".".repeat(calcDots(sugars, requiredDots)),
    position: {
      x: x,
      y: Math.round(nutriDataCarbs.position.y + nutriDataCarbs.size.height),
    },
    size: { height: Math.round(ptToInch(6) * dpi) },
    type: "ZebraText",
  };

  const nutriDataSugarsValue: ZebraTextBlock = {
    ...nutriDataEnergyValue,
    content: label.product.sugar + " g",
    position: {
      x: x + Math.round(mmToInch(rightX) * dpi),
      y: nutriDataSugars.position.y,
    },
  };

  const protein = "Protein ";
  const nutriDataProtein: ZebraText = {
    content: protein + ".".repeat(calcDots(protein, requiredDots)),
    position: {
      x: x,
      y: Math.round(nutriDataSugars.position.y + nutriDataSugars.size.height),
    },
    size: { height: Math.round(ptToInch(6) * dpi) },
    type: "ZebraText",
  };

  const nutriDataProteinValue: ZebraTextBlock = {
    ...nutriDataEnergyValue,
    content: label.product.protein + " g",
    position: {
      x: x + Math.round(mmToInch(rightX) * dpi),
      y: nutriDataProtein.position.y,
    },
  };

  const salt = "Sodium ";
  const nutriDataSalt: ZebraText = {
    content: salt + ".".repeat(calcDots(salt, requiredDots)),
    position: {
      x: x,
      y: Math.round(nutriDataProtein.position.y + nutriDataProtein.size.height),
    },
    size: { height: Math.round(ptToInch(6) * dpi) },
    type: "ZebraText",
  };

  const nutriDataSaltValue: ZebraTextBlock = {
    ...nutriDataEnergyValue,
    content: label.product.salt + " g",
    position: {
      x: x + Math.round(mmToInch(rightX) * dpi),
      y: nutriDataSalt.position.y,
    },
  };

  const origin: ZebraText = {
    content: "Origin: " + translatedOrigin(),
    position: { x: x, y: Math.round(mmToInch(59 + positionAdjust) * dpi) },
    size: { height: Math.round(ptToInch(7) * dpi) },
    type: "ZebraText",
  };
  let bestBefore: ZebraText = {
    content:
      "Best before: " + moment(new Date(dto.bestBefore)).format("DD.MM.YYYY"),
    position: { x: x, y: Math.round(origin.position.y + origin.size.height) },
    size: { height: Math.round(ptToInch(7) * dpi) },
    type: "ZebraText",
  };
  let lot: ZebraText = {
    content: "Lot: " + dto.lot,
    position: {
      x: x,
      y: Math.round(bestBefore.position.y + bestBefore.size.height),
    },
    size: { height: Math.round(ptToInch(7) * dpi) },
    type: "ZebraText",
  };
  console.log("lot-----", dto);
  let keepAt: ZebraText = {
    content: "Store at: -20°C...-18°C",
    position: { x: x, y: Math.round(lot.position.y + lot.size.height) },
    size: { height: Math.round(ptToInch(7) * dpi) },
    type: "ZebraText",
  };
  let doNotFreeze: ZebraText = {
    content: "Do not freeze after thawing!",
    position: { x: x, y: Math.round(keepAt.position.y + keepAt.size.height) },
    size: { height: Math.round(ptToInch(7) * dpi) },
    type: "ZebraText",
  };

  const packagerTitle = getPackagerTitle();
  let packager: ZebraTextBlock = {
    content:
      packagerTitle +
      ": OÜ Figuraata, Jõe 11B, Räpina, Põlvamaa 64505, Estonia",
    position: {
      x: x,
      y: Math.round(doNotFreeze.position.y + doNotFreeze.size.height),
    },
    maxLines: 2,
    maxWidth: (500 * dpi) / 300,
    alignment: Alignment.LEFT,
    size: { height: Math.round(ptToInch(7) * dpi) },
    type: "ZebraTextBlock",
  };

  const barcode: ZebraBarcode = {
    barcodeType: BARCODE_TYPE.EAN13,
    orientation: Orientation.ROTATED_0,
    code: label.barcode,
    position: { x: x + 30, y: Math.round(mmToInch(77 + positionAdjust) * dpi) },
    size: { height: Math.round(mmToInch(9.5) * dpi) },
    type: "ZebraBarcode",
  };

  let weightHeight = Math.round(ptToInch(18) * dpi);
  let weightValue;

  if (dto.weight === 500) {
    weightValue = "500 g";
  } else if (dto.weight === 1000) {
    weightValue = "1 kg";
  } else if (dto.weight === 2500) {
    weightValue = "2.5 kg";
  } else if (dto.weight === 500 * 1000) {
    weightValue = "500 kg";
  } else if (dto.weight === 10 * 1000) {
    weightValue = "10 kg";
  } else if (dto.weight > 1000) {
    weightValue = dto.weight / 1000 + " kg";
  } else if (dto.weight === -1) {
    weightValue = "";
  } else {
    weightValue = dto.weight + " g";
  }

  let weight: ZebraTextBlock = {
    alignment: Alignment.CENTER,
    content: weightValue,
    maxLines: 1,
    maxWidth: Math.round(mmToInch(22) * dpi),
    position: {
      x: Math.round(mmToInch(60) * dpi),
      y: Math.round(mmToInch(81 + positionAdjust) * dpi),
    },
    size: { height: weightHeight },
    type: "ZebraTextBlock",
  };

  if (dto.isBoxLabel) {
    weightHeight = Math.round(ptToInch(12) * dpi);
    let unitWeight = "500 g";
    if (label.weight === 2500) {
      unitWeight = "2.5 kg";
      weightHeight = Math.round(ptToInch(11) * dpi);
    } else {
      unitWeight = "1 kg";
    }

    weightValue = `${dto.unitSize} kg\\&(${unitWeight} x ${
      (dto.unitSize * 1000) / label.weight
    })`;

    weight = {
      ...weight,
      content: weightValue,
      maxLines: 2,
      position: {
        ...weight.position,
        y: weight.position.y - Math.round(mmToInch(2) * dpi),
      },
      size: { height: weightHeight },
    };
  }

  if (label.barcode === "-1") {
    bestBefore = {
      ...bestBefore,
      position: { x: x, y: Math.round(mmToInch(80 + positionAdjust) * dpi) },
      size: { height: Math.round(ptToInch(11) * dpi) },
    };
    lot = {
      ...lot,
      position: {
        x: x,
        y: Math.round(bestBefore.position.y + bestBefore.size.height),
      },
      size: { height: Math.round(ptToInch(11) * dpi) },
    };
    keepAt = {
      ...keepAt,
      position: { x: x, y: Math.round(origin.position.y + origin.size.height) },
    };
    doNotFreeze = {
      ...doNotFreeze,
      position: { x: x, y: Math.round(keepAt.position.y + keepAt.size.height) },
    };
    packager = {
      ...packager,
      position: {
        x: x,
        y: Math.round(doNotFreeze.position.y + doNotFreeze.size.height),
      },
    };
  }

  let labelParts: ZebraItem[] = [
    title,
    nutriDataTitle,
    nutriDataEnergy,
    nutriDataEnergyValue,
    nutriDataFat,
    nutriDataFatValue,
    nutriDataSatFat,
    nutriDataSatFatValue,
    nutriDataCarbs,
    nutriDataCarbsValue,
    nutriDataSugars,
    nutriDataSugarsValue,
    nutriDataProtein,
    nutriDataProteinValue,
    nutriDataSalt,
    nutriDataSaltValue,
    origin,
    bestBefore,
    lot,
    keepAt,
    doNotFreeze,
    packager,
    // weight,
  ];
  if (label.barcode !== "-1") {
    labelParts.push(barcode);
  }

  if (label.weight > 0) {
    labelParts.push(weight);
  }
  if (label.isOrganic) {
    labelParts.push(flag, eeoko03);
  }

  console.log("DTO", dto, lot);

  //labelParts = [pngimg]

  const builtLabel = PrinterService.buildLabel(
    {
      labelWidth: labelWidthDots,
      orientation: Orientation.ROTATED_0,
      labelHeight: labelHeightDots,
      quantity: dto.quantity,
      flippedHorizontally: label.flippedHorizontally,
    },
    labelParts
  );
  return builtLabel;
};

export { Template as build };
